import { BaseService } from "../api/BaseService";
import type {
  FinalResponse,
  CreateCategoryRequest,
  UpdateCategoryRequest,
  CreateEstablishmentRequest,
  UpdateEstablishmentRequest,
  CreateOpeningHoursRequest,
  UpdateOpeningHoursRequest,
  CreateServicesRequest,
  UpdateServicesRequest,
  CreateSpaceRequest,
  UpdateSpaceRequest,
  CreateUnitTypeRequest,
  UpdateUnitTypeRequest,
  CreateVacationRequest,
  UpdateVacationRequest,
  CreateVerificationRequest,
  UpdateVerificationRequest,
  CategoryResponse,
  EstablishmentResponse,
  OpeningHoursResponse,
  ServicesResponse,
  SpaceResponse,
  UnitTypeResponse,
  VacationResponse,
  VerificationResponse,
  ListCategoryResponse,
  ListEstablishmentResponse,
  ListOpeningHoursResponse,
  ListServicesResponse,
  ListSpaceResponse,
  ListUnitTypeResponse,
  ListVacationResponse,
  ListVerificationResponse,
  CategoryFilter,
  EstablishmentFilter,
  OpeningHoursFilter,
  ServicesFilter,
  SpaceFilter,
  UnitTypeFilter,
  VacationFilter,
  VerificationFilter,
  VerificationStep,
  EstablishmentInternalInfo,
  PatchVerificationStepRequest,
  AddImageResponse,
  ListImageEstablishmentResponse,
} from "../models";

export class EstablishmentService extends BaseService {
  private static readonly SERVICE = "Establishment";

  // Category methods
  async createCategory(data: CreateCategoryRequest): Promise<CategoryResponse> {
    const res = await this.api.request<FinalResponse<CategoryResponse>>(
      "/establishment/category",
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
    return this.unwrap(res, EstablishmentService.SERVICE);
  }

  async updateCategory(
    id: string,
    data: UpdateCategoryRequest,
  ): Promise<CategoryResponse> {
    const res = await this.api.request<FinalResponse<CategoryResponse>>(
      `/establishment/category/${id}`,
      {
        method: "PUT",
        body: JSON.stringify(data),
      },
    );
    return this.unwrap(res, EstablishmentService.SERVICE);
  }

  async deleteCategory(id: string): Promise<void> {
    const res = await this.api.request<FinalResponse<void>>(
      `/establishment/category/${id}`,
      {
        method: "DELETE",
      },
    );
    this.unwrap(res, EstablishmentService.SERVICE);
  }

  async fetchCategory(id: string): Promise<CategoryResponse> {
    const res = await this.api.request<FinalResponse<CategoryResponse>>(
      `/establishment/category/${id}`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, EstablishmentService.SERVICE);
  }

  async listCategories(
    page: number = 1,
    limit: number = 10,
    filter?: CategoryFilter,
  ): Promise<ListCategoryResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (filter?.name) params.append("name", filter.name);

    const res = await this.api.request<FinalResponse<ListCategoryResponse>>(
      `/establishment/category?${params}`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, EstablishmentService.SERVICE);
  }

  // Establishment methods
  async createEstablishment(
    data: CreateEstablishmentRequest,
  ): Promise<EstablishmentResponse> {
    const res = await this.api.request<FinalResponse<EstablishmentResponse>>(
      "/establishment/establishment",
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
    return this.unwrap(res, EstablishmentService.SERVICE);
  }

  async updateEstablishment(
    id: string,
    data: UpdateEstablishmentRequest,
  ): Promise<EstablishmentResponse> {
    const res = await this.api.request<FinalResponse<EstablishmentResponse>>(
      `/establishment/establishment/${id}`,
      {
        method: "PUT",
        body: JSON.stringify(data),
      },
    );
    return this.unwrap(res, EstablishmentService.SERVICE);
  }

  async deleteEstablishment(id: string): Promise<void> {
    const res = await this.api.request<FinalResponse<void>>(
      `/establishment/establishment/${id}`,
      {
        method: "DELETE",
      },
    );
    this.unwrap(res, EstablishmentService.SERVICE);
  }

  async fetchEstablishment(id: string): Promise<EstablishmentResponse> {
    const res = await this.api.request<FinalResponse<EstablishmentResponse>>(
      `/establishment/establishment/${id}`,
      {
        method: "GET",
      },
    );
    return this.unwrapDetail(res, EstablishmentService.SERVICE);
  }

  async listEstablishments(
    page: number = 1,
    limit: number = 10,
    filter?: EstablishmentFilter,
  ): Promise<ListEstablishmentResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (filter?.name) params.append("name", filter.name);
    if (filter?.categoryId)
      filter.categoryId.forEach((id) => params.append("category_id", id));
    if (filter?.cityUuid) params.append("city_uuid", filter.cityUuid);
    if (filter?.countryUuid) params.append("country_uuid", filter.countryUuid);
    if (filter?.latitude !== undefined)
      params.append("latitude", filter.latitude.toString());
    if (filter?.longitude !== undefined)
      params.append("longitude", filter.longitude.toString());
    if (filter?.radius !== undefined)
      params.append("radius", filter.radius.toString());
    if (filter?.openAtDate) params.append("open_at_date", filter.openAtDate);
    if (filter?.openAtTime) params.append("open_at_time", filter.openAtTime);
    if (filter?.timeZone) params.append("timezone", filter.timeZone);

    const res = await this.api.request<
      FinalResponse<ListEstablishmentResponse>
    >(`/establishment/establishment?${params}`, {
      method: "GET",
    });
    return this.unwrap(res, EstablishmentService.SERVICE);
  }

  async getEstablishmentsByUserID(
    userID: string,
  ): Promise<EstablishmentResponse[]> {
    const res = await this.api.request<FinalResponse<EstablishmentResponse[]>>(
      `/establishment/establishment/user/${userID}`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, EstablishmentService.SERVICE);
  }

  // OpeningHours methods
  async createOpeningHours(
    data: CreateOpeningHoursRequest,
  ): Promise<OpeningHoursResponse> {
    const res = await this.api.request<FinalResponse<OpeningHoursResponse>>(
      "/establishment/opening-hours",
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
    return this.unwrap(res, EstablishmentService.SERVICE);
  }

  async updateOpeningHours(
    id: string,
    data: UpdateOpeningHoursRequest,
  ): Promise<OpeningHoursResponse> {
    const res = await this.api.request<FinalResponse<OpeningHoursResponse>>(
      `/establishment/opening-hours/${id}`,
      {
        method: "PUT",
        body: JSON.stringify(data),
      },
    );
    return this.unwrap(res, EstablishmentService.SERVICE);
  }

  async deleteOpeningHours(id: string): Promise<void> {
    const res = await this.api.request<FinalResponse<void>>(
      `/establishment/opening-hours/${id}`,
      {
        method: "DELETE",
      },
    );
    this.unwrap(res, EstablishmentService.SERVICE);
  }

  async fetchOpeningHours(id: string): Promise<OpeningHoursResponse> {
    const res = await this.api.request<FinalResponse<OpeningHoursResponse>>(
      `/establishment/opening-hours/${id}`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, EstablishmentService.SERVICE);
  }

  async listOpeningHours(
    page: number = 1,
    limit: number = 10,
    filter?: OpeningHoursFilter,
  ): Promise<ListOpeningHoursResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (filter?.spaceId) params.append("space_id", filter.spaceId);

    const res = await this.api.request<FinalResponse<ListOpeningHoursResponse>>(
      `/establishment/opening-hours?${params}`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, EstablishmentService.SERVICE);
  }

  // Services methods
  async createServices(data: CreateServicesRequest): Promise<ServicesResponse> {
    const res = await this.api.request<FinalResponse<ServicesResponse>>(
      "/establishment/services",
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
    return this.unwrap(res, EstablishmentService.SERVICE);
  }

  async updateServices(
    id: string,
    data: UpdateServicesRequest,
  ): Promise<ServicesResponse> {
    const res = await this.api.request<FinalResponse<ServicesResponse>>(
      `/establishment/services/${id}`,
      {
        method: "PUT",
        body: JSON.stringify(data),
      },
    );
    return this.unwrap(res, EstablishmentService.SERVICE);
  }

  async deleteServices(id: string): Promise<void> {
    const res = await this.api.request<FinalResponse<void>>(
      `/establishment/services/${id}`,
      {
        method: "DELETE",
      },
    );
    this.unwrap(res, EstablishmentService.SERVICE);
  }

  async fetchServices(id: string): Promise<ServicesResponse> {
    const res = await this.api.request<FinalResponse<ServicesResponse>>(
      `/establishment/services/${id}`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, EstablishmentService.SERVICE);
  }

  async listServices(
    page: number = 1,
    limit: number = 10,
    filter?: ServicesFilter,
  ): Promise<ListServicesResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (filter?.spaceId) params.append("space_id", filter.spaceId);

    const res = await this.api.request<FinalResponse<ListServicesResponse>>(
      `/establishment/services?${params}`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, EstablishmentService.SERVICE);
  }

  // Space methods
  async createSpace(data: CreateSpaceRequest): Promise<SpaceResponse> {
    const res = await this.api.request<FinalResponse<SpaceResponse>>(
      "/establishment/space",
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
    return this.unwrap(res, EstablishmentService.SERVICE);
  }

  async updateSpace(
    id: string,
    data: UpdateSpaceRequest,
  ): Promise<SpaceResponse> {
    const res = await this.api.request<FinalResponse<SpaceResponse>>(
      `/establishment/space/${id}`,
      {
        method: "PUT",
        body: JSON.stringify(data),
      },
    );
    return this.unwrap(res, EstablishmentService.SERVICE);
  }

  async deleteSpace(id: string): Promise<void> {
    const res = await this.api.request<FinalResponse<void>>(
      `/establishment/space/${id}`,
      {
        method: "DELETE",
      },
    );
    this.unwrap(res, EstablishmentService.SERVICE);
  }

  async fetchSpace(id: string): Promise<SpaceResponse> {
    const res = await this.api.request<FinalResponse<SpaceResponse>>(
      `/establishment/space/${id}`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, EstablishmentService.SERVICE);
  }

  async listSpaces(
    page: number = 1,
    limit: number = 10,
    filter?: SpaceFilter,
  ): Promise<ListSpaceResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (filter?.establishmentId)
      params.append("establishment_id", filter.establishmentId);

    const res = await this.api.request<FinalResponse<ListSpaceResponse>>(
      `/establishment/space?${params}`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, EstablishmentService.SERVICE);
  }

  // UnitType methods
  async createUnitType(data: CreateUnitTypeRequest): Promise<UnitTypeResponse> {
    const res = await this.api.request<FinalResponse<UnitTypeResponse>>(
      "/establishment/unit-type",
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
    return this.unwrap(res, EstablishmentService.SERVICE);
  }

  async updateUnitType(
    id: string,
    data: UpdateUnitTypeRequest,
  ): Promise<UnitTypeResponse> {
    const res = await this.api.request<FinalResponse<UnitTypeResponse>>(
      `/establishment/unit-type/${id}`,
      {
        method: "PUT",
        body: JSON.stringify(data),
      },
    );
    return this.unwrap(res, EstablishmentService.SERVICE);
  }

  async deleteUnitType(id: string): Promise<void> {
    const res = await this.api.request<FinalResponse<void>>(
      `/establishment/unit-type/${id}`,
      {
        method: "DELETE",
      },
    );
    this.unwrap(res, EstablishmentService.SERVICE);
  }

  async fetchUnitType(id: string): Promise<UnitTypeResponse> {
    const res = await this.api.request<FinalResponse<UnitTypeResponse>>(
      `/establishment/unit-type/${id}`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, EstablishmentService.SERVICE);
  }

  async listUnitTypes(
    page: number = 1,
    limit: number = 10,
    filter?: UnitTypeFilter,
  ): Promise<ListUnitTypeResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (filter?.spaceId) params.append("space_id", filter.spaceId);

    const res = await this.api.request<FinalResponse<ListUnitTypeResponse>>(
      `/establishment/unit-type?${params}`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, EstablishmentService.SERVICE);
  }

  // Vacation methods
  async createVacation(data: CreateVacationRequest): Promise<VacationResponse> {
    const res = await this.api.request<FinalResponse<VacationResponse>>(
      "/establishment/vacation",
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
    return this.unwrap(res, EstablishmentService.SERVICE);
  }

  async updateVacation(
    id: string,
    data: UpdateVacationRequest,
  ): Promise<VacationResponse> {
    const res = await this.api.request<FinalResponse<VacationResponse>>(
      `/establishment/vacation/${id}`,
      {
        method: "PUT",
        body: JSON.stringify(data),
      },
    );
    return this.unwrap(res, EstablishmentService.SERVICE);
  }

  async deleteVacation(id: string): Promise<void> {
    const res = await this.api.request<FinalResponse<void>>(
      `/establishment/vacation/${id}`,
      {
        method: "DELETE",
      },
    );
    this.unwrap(res, EstablishmentService.SERVICE);
  }

  async fetchVacation(id: string): Promise<VacationResponse> {
    const res = await this.api.request<FinalResponse<VacationResponse>>(
      `/establishment/vacation/${id}`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, EstablishmentService.SERVICE);
  }

  async listVacations(
    page: number = 1,
    limit: number = 10,
    filter?: VacationFilter,
  ): Promise<ListVacationResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (filter?.spaceId) params.append("space_id", filter.spaceId);
    if (filter?.start) params.append("start", filter.start);
    if (filter?.end) params.append("end", filter.end);

    const res = await this.api.request<FinalResponse<ListVacationResponse>>(
      `/establishment/vacation?${params}`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, EstablishmentService.SERVICE);
  }

  // Verification methods
  async createVerification(
    data: CreateVerificationRequest,
  ): Promise<VerificationResponse> {
    const res = await this.api.request<FinalResponse<VerificationResponse>>(
      "/establishment/verification",
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
    return this.unwrap(res, EstablishmentService.SERVICE);
  }

  async updateVerification(
    id: string,
    data: UpdateVerificationRequest,
  ): Promise<VerificationResponse> {
    const res = await this.api.request<FinalResponse<VerificationResponse>>(
      `/establishment/verification/${id}`,
      {
        method: "PUT",
        body: JSON.stringify(data),
      },
    );
    return this.unwrap(res, EstablishmentService.SERVICE);
  }

  async claimVerification(id: string): Promise<VerificationResponse> {
    const res = await this.api.request<FinalResponse<VerificationResponse>>(
      `/establishment/verification/${id}/claim`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json; charset=utf-8" },
      },
    );
    return this.unwrap(res, EstablishmentService.SERVICE);
  }

  async reclaimVerification(id: string): Promise<VerificationResponse> {
    const res = await this.api.request<FinalResponse<VerificationResponse>>(
      `/establishment/verification/${id}/reclaim`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json; charset=utf-8" },
      },
    );
    return this.unwrap(res, EstablishmentService.SERVICE);
  }

  async deleteVerification(id: string): Promise<void> {
    const res = await this.api.request<FinalResponse<void>>(
      `/establishment/verification/${id}`,
      {
        method: "DELETE",
      },
    );
    this.unwrap(res, EstablishmentService.SERVICE);
  }

  async fetchVerification(id: string): Promise<VerificationResponse> {
    const res = await this.api.request<FinalResponse<VerificationResponse>>(
      `/establishment/verification/${id}`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, EstablishmentService.SERVICE);
  }

  async listVerifications(
    page: number = 1,
    limit: number = 10,
    filter?: VerificationFilter,
  ): Promise<ListVerificationResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (filter?.establishmentId != null)
      params.append("establishment_id", filter.establishmentId);
    if (filter?.states) {
      filter.states.forEach((state) => params.append("states", state));
    }

    const res = await this.api.request<FinalResponse<ListVerificationResponse>>(
      `/establishment/verification?${params}`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, EstablishmentService.SERVICE);
  }

  // Additional methods for BFF
  async fetchEstablishmentInternal(
    id: string,
  ): Promise<EstablishmentInternalInfo> {
    const res = await this.api.request<
      FinalResponse<EstablishmentInternalInfo>
    >(`/establishment/establishment/${id}/internal`, {
      method: "GET",
    });
    return this.unwrap(res, EstablishmentService.SERVICE);
  }

  async updateEstablishmentInternal(
    id: string,
    data: Partial<EstablishmentInternalInfo>,
  ): Promise<EstablishmentInternalInfo> {
    const res = await this.api.request<
      FinalResponse<EstablishmentInternalInfo>
    >(`/establishment/establishment/${id}/internal`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
    return this.unwrap(res, EstablishmentService.SERVICE);
  }

  async getVerificationSteps(
    verificationId: string,
  ): Promise<VerificationStep[]> {
    const res = await this.api.request<FinalResponse<VerificationStep[]>>(
      `/establishment/verification/${verificationId}/steps`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, EstablishmentService.SERVICE);
  }

  async patchVerificationStep(
    verificationId: string,
    data: PatchVerificationStepRequest,
  ): Promise<VerificationStep> {
    const { id, ...payload } = data;
    const res = await this.api.request<FinalResponse<VerificationStep>>(
      `/establishment/verification/${verificationId}/steps/${id}`,
      {
        method: "PATCH",
        body: JSON.stringify(payload),
      },
    );
    return this.unwrap(res, EstablishmentService.SERVICE);
  }

  // Image routes
  async addImageToEstablishment(
    establishmentId: string,
    data: {
      image: Blob | File;
      userId: string;
      visibility?: string;
      width?: number;
      height?: number;
      angle?: number;
      transformType?: string;
      format?: string;
      caption?: string;
      tags?: string[];
    },
  ): Promise<AddImageResponse> {
    const form = new FormData();
    const fileName = data.image instanceof File ? data.image.name : "image.jpg";

    form.append("image", data.image as Blob, fileName);
    form.append("user_id", data.userId);
    if (data.visibility) form.append("visibility", data.visibility);
    if (data.width !== undefined) form.append("width", data.width.toString());
    if (data.height !== undefined)
      form.append("height", data.height.toString());
    if (data.angle !== undefined) form.append("angle", data.angle.toString());
    if (data.transformType) form.append("transform_type", data.transformType);
    if (data.format) form.append("format", data.format);
    if (data.caption) form.append("caption", data.caption);
    if (data.tags) data.tags.forEach((t) => form.append("tags", t));

    const res = await this.api.request<FinalResponse<AddImageResponse>>(
      `/establishment/establishment/${establishmentId}/image`,
      {
        method: "POST",
        body: form,
      },
    );
    return this.unwrap(res, EstablishmentService.SERVICE);
  }

  async removeImageFromEstablishment(
    establishmentId: string,
    imageId: string,
  ): Promise<void> {
    const res = await this.api.request<FinalResponse<void>>(
      `/establishment/establishment/${establishmentId}/image/${imageId}`,
      {
        method: "DELETE",
      },
    );
    this.unwrap(res, EstablishmentService.SERVICE);
  }

  async listImagesFromEstablishment(
    establishmentId: string,
  ): Promise<ListImageEstablishmentResponse> {
    const res = await this.api.request<
      FinalResponse<ListImageEstablishmentResponse>
    >(`/establishment/establishment/${establishmentId}/image`, {
      method: "GET",
    });
    return this.unwrap(res, EstablishmentService.SERVICE);
  }

  async reorderEstablishmentImages(
    establishmentId: string,
    imageIds: string[],
  ): Promise<void> {
    const res = await this.api.request<FinalResponse<void>>(
      `/establishment/establishment/${establishmentId}/image/reorder`,
      {
        method: "PUT",
        body: JSON.stringify({ imageIds }),
        headers: { "Content-Type": "application/json; charset=utf-8" },
      },
    );
    this.unwrap(res, EstablishmentService.SERVICE);
  }

  // end image routes
}
